import { useMutation, useQueryClient } from '@tanstack/react-query';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';

import { chatEndpoints } from './constants';
import { chatMutationKeys, chatQueryKeys } from './queryKeys';
import { refreshJobData } from './refreshJobData';
import type { ChatDecision } from './replyResults/ConfirmationCard';

export interface DecideChatVariables {
  chatId: string;
  confirmationId: string;
  decision: ChatDecision;
}

/**
 * Runs a click on Confirm or Cancel under a change Finlay proposed, without asking Finlay again. finlay-api answers
 * with the chat, the click and its outcome recorded in it.
 */
export const useDecideChatMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: chatMutationKeys.decide,
    mutationFn: async ({
      chatId,
      confirmationId,
      decision,
    }: DecideChatVariables) => {
      const { data } = await fetchClient.POST(chatEndpoints.actions, {
        params: { path: { id: chatId } },
        body: { confirmationId, decision },
      });
      return data;
    },
    onSuccess: async (chat, { chatId }) => {
      const queryKey = chatQueryKeys.detail(chatId);
      await queryClient.cancelQueries({ queryKey });
      if (chat) queryClient.setQueryData(queryKey, chat);
      // The change may have moved a candidate or changed a job.
      void refreshJobData(queryClient);
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: chatQueryKeys.lists() }),
  });
};
